import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import './EventDetailsModal.css';
import { apiRequest } from '../../utils';
import translations from '../../lang';

const EventDetailsModal = ({
  isOpen,
  onClose,
  eventDetails,
  onDelete,
  onMoveBooking,
  onMoveAppointment,
  onBook,
  onDeleteBooking,
  fetchAppointments
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [blockingUserId, setBlockingUserId] = useState(null);
  const [blockingBookingId, setBlockingBookingId] = useState(null);
  const [isBooking, setIsBooking] = useState(false);
  const [isMovingAppointment, setisMovingAppointment] = useState(false);
  const [isMovingBooks, setIsMovingBooks] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedBookingIndex, setSelectedBookingIndex] = useState(null);
  const [name, setName] = useState('');

  // ✅ kiválasztott kliens (booking) a csomaglistához
  const [selectedClientBookingId, setSelectedClientBookingId] = useState('');
  const [clientPackages, setClientPackages] = useState([]);
  const [clientPackagesLoading, setClientPackagesLoading] = useState(false);

  // ✅ lokális bookings state (NE módosíts props-ot)
  const [localBookings, setLocalBookings] = useState([]);

  const language = localStorage.getItem('language') || 'ro';
  const t = translations[language];

  // ✅ helper: userId kinyerés többféle serializer formátumhoz
  const getBookingUserId = (booking) => {
    if (!booking) return null;

    // gyakori formák:
    // 1) user: 12
    if (booking.user && typeof booking.user === 'number') return booking.user;

    // 2) user_id: 12
    if (booking.user_id && typeof booking.user_id === 'number') return booking.user_id;

    // 3) user: "12"
    if (booking.user && typeof booking.user === 'string' && booking.user.trim() !== '') {
      const n = Number(booking.user);
      return Number.isFinite(n) ? n : null;
    }

    // 4) user_id: "12"
    if (booking.user_id && typeof booking.user_id === 'string' && booking.user_id.trim() !== '') {
      const n = Number(booking.user_id);
      return Number.isFinite(n) ? n : null;
    }

    // 5) user: { id: 12 }
    if (booking.user && typeof booking.user === 'object' && booking.user !== null) {
      const maybeId = booking.user.id ?? booking.user.pk;
      if (maybeId !== undefined && maybeId !== null) {
        const n = Number(maybeId);
        return Number.isFinite(n) ? n : null;
      }
    }

    return null;
  };

  // ✅ amikor megnyílik a modal / event vált: másoljuk be a bookings-t lokális state-be
  useEffect(() => {
    if (!isOpen) return;

    const bks = Array.isArray(eventDetails?.bookings) ? eventDetails.bookings : [];
    setLocalBookings(bks);

    // alapból az első foglalót kiválasztjuk csomaglistához
    if (bks.length > 0) setSelectedClientBookingId(String(bks[0]?.id || ''));
    else setSelectedClientBookingId('');

    setClientPackages([]);
  }, [isOpen, eventDetails]);

  // ✅ kliens csomagok betöltése (kiválasztott booking alapján)
  useEffect(() => {
    const loadClientPackages = async () => {
      if (!isOpen) return;
      if (!eventDetails?.id) return;

      if (!selectedClientBookingId) {
        setClientPackages([]);
        return;
      }

      const booking = (localBookings || []).find(
        (b) => String(b.id) === String(selectedClientBookingId)
      );

      const userId = getBookingUserId(booking);

      // book-by-name esetén nincs user -> nincs csomaglista
      if (!userId) {
        setClientPackages([]);
        return;
      }

      setClientPackagesLoading(true);

      try {
        const res = await apiRequest(
          `${process.env.REACT_APP_API}/api/appointment-packages-by-user/${eventDetails.id}/?user_id=${userId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
              'Accept-Language': language,
            },
          }
        );

        const data = await res.json().catch(() => []);
        setClientPackages(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching client packages:', error);
        setClientPackages([]);
      } finally {
        setClientPackagesLoading(false);
      }
    };

    loadClientPackages();
  }, [isOpen, eventDetails, selectedClientBookingId, language, localBookings]);

  useEffect(() => {
    const serviceId = eventDetails?.service?.id || eventDetails?.service_id;
    if (isMovingBooks && serviceId) {
      fetchAvailableSlotsForBooking(serviceId);
    }
  }, [isMovingBooks, eventDetails]);

  const fetchAvailableSlotsForBooking = (serviceId) => {
    let url = `${process.env.REACT_APP_API}/api/available-slots-by-user-service-for-book/`;
    if (serviceId) url += `?service_id=${serviceId}`;

    fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        'Accept-Language': language,
      },
    })
      .then((response) => response.json())
      .then((data) => setAvailableSlots(data.slots || []))
      .catch((error) => {
        console.error('Error fetching available slots for booking:', error);
        setAvailableSlots([]);
      });
  };

  useEffect(() => {
    const serviceId = eventDetails?.service?.id || eventDetails?.service_id;
    if (isMovingAppointment && serviceId) {
      fetchAvailableSlots(serviceId);
    }
  }, [isMovingAppointment, eventDetails]);

  const fetchAvailableSlots = (serviceId) => {
    let url = `${process.env.REACT_APP_API}/api/available-slots-by-user-service/`;
    if (serviceId) url += `?service_id=${serviceId}`;

    fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        'Accept-Language': language,
      },
    })
      .then((response) => response.json())
      .then((data) => setAvailableSlots(data.slots || []))
      .catch((error) => {
        console.error('Error fetching available slots for appointment:', error);
        setAvailableSlots([]);
      });
  };

  const handleConfirmDelete = () => {
    onDelete();
    setShowConfirm(false);
    onClose();
  };

  const handleMoveBookingClick = (index) => {
    setSelectedBookingIndex(index);
    setIsMovingBooks(true);
  };

  const handleMoveBookingSubmit = (slotId) => {
    const bookingId = localBookings[selectedBookingIndex].id;
    onMoveBooking({ new_slot_id: slotId, booking_id: bookingId });
    setIsMovingBooks(false);
    onClose();
  };

  const handleMoveAppointmentSubmit = (slotId) => {
    onMoveAppointment({ new_slot_id: slotId, current_appointment_id: eventDetails.id });
    setisMovingAppointment(false);
    onClose();
  };

  const handleBlockClient = (userId, bookingId) => {
    setBlockingUserId(userId);
    setBlockingBookingId(bookingId);
    setShowBlockConfirm(true);
  };

  const confirmBlockClient = () => {
    apiRequest(`${process.env.REACT_APP_API}/api/block-client/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        'Accept-Language': language,
      },
      body: JSON.stringify({ user_id: blockingUserId })
    })
      .then(response => response.json())
      .then(data => {
        if (!data.success) {
          alert(data?.error || t.update_status_failed);
        } else {
          alert(t.client_blocked_successfully);
          onDeleteBooking(blockingBookingId);

          // ✅ UI azonnali frissítés: töröljük lokálisan is
          setLocalBookings((prev) => prev.filter((b) => b.id !== blockingBookingId));
          fetchAppointments();
        }
      })
      .catch((error) => console.error('Hiba a kliens tiltása során:', error))
      .finally(() => {
        setShowBlockConfirm(false);
        setBlockingUserId(null);
        setBlockingBookingId(null);
      });
  };

  const handleClose = () => {
    setIsBooking(false);
    setisMovingAppointment(false);
    setIsMovingBooks(false);
    setShowConfirm(false);
    setShowBlockConfirm(false);
    onClose();
  };

  // ✅ status frissítés: lokális state + fetchAppointments
  const handleChangeStatus = async (newStatus, bookingId) => {
    try {
      const res = await apiRequest(
        `${process.env.REACT_APP_API}/api/bookings/${bookingId}/update-status/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Accept-Language': language,
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (res.ok && data.success) {
        setLocalBookings((prev) =>
          prev.map((b) => (b.id === bookingId ? { ...b, status: newStatus } : b))
        );
        fetchAppointments();
      } else {
        alert(data?.error || t.update_status_failed);
      }
    } catch (error) {
      console.error('Hiba a státusz frissítése során:', error);
      alert(t.update_status_failed);
    }
  };

  const calendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    initialView: 'timeGridWeek',
    headerToolbar: { left: '', right: 'prev,next today' },
    slotMinTime: '06:00:00',
    allDaySlot: false,
    events: availableSlots.map((slot) => ({
      id: slot.id,
      title: t.free,
      start: new Date(slot.start_time),
      end: new Date(slot.end_time),
      backgroundColor: 'green',
    })),
    selectable: true,
    firstDay: 1,
    locale: language,
    buttonText: { today: t.today, month: t.month, week: t.week, day: t.day },
    select: (info) => {
      const selectedSlotId = info.startStr;
      if (isMovingBooks && localBookings?.length > 0) handleMoveBookingSubmit(selectedSlotId);
    },
    eventClick: (info) => {
      const selectedSlotId = info.event.id;
      if (isMovingBooks && localBookings?.length > 0) handleMoveBookingSubmit(selectedSlotId);
      if (isMovingAppointment) handleMoveAppointmentSubmit(selectedSlotId);
    },
  };

  if (!isOpen) return null;

  // ✅ kiválasztott booking és annak userId-ja (a UI üzenetekhez)
  const selectedBooking = (localBookings || []).find(
    (b) => String(b.id) === String(selectedClientBookingId)
  );
  const selectedUserId = getBookingUserId(selectedBooking);

  return (
    <div className="modal">
      <div className="modal-content">
        <button className="close-icon" onClick={handleClose}>×</button>

        <p><strong>{t.service}:</strong> {eventDetails.title}</p>
        <p><strong>{t.start_time}:</strong> {new Date(eventDetails.start).toLocaleString()}</p>
        <p><strong>{t.end_time}:</strong> {new Date(eventDetails.end).toLocaleString()}</p>

        {!isMovingBooks && !isMovingAppointment && localBookings?.length > 0 && (
          <>
            <table className="bookings-table">
              <thead>
                <tr>
                  <th>{t.name}</th>
                  <th>{t.actions}</th>
                </tr>
              </thead>
              <tbody>
                {localBookings.map((booking, index) => {
                  let statusSymbol = '';
                  if (booking.status === 'Confirmed' || booking.status === 'confirmed') statusSymbol = '✅';
                  else if (booking.status === 'Rejected' || booking.status === 'rejected') statusSymbol = '❌';
                  else if (booking.status === 'Pending' || booking.status === 'pending') statusSymbol = '⚠️';

                  return (
                    <tr key={booking.id ?? index}>
                      <td>{booking.booked_by_name} {statusSymbol}</td>
                      <td>
                        <button onClick={() => handleChangeStatus('Confirmed', booking.id)} className="accept-btn">
                          {t.accept}
                        </button>

                        <button onClick={() => handleChangeStatus('Rejected', booking.id)} className="refuse-btn">
                          {t.reject}
                        </button>

                        <button
                          onClick={() => {
                            onDeleteBooking(booking.id);
                            setLocalBookings((prev) => prev.filter((b) => b.id !== booking.id));
                            fetchAppointments();
                          }}
                          className="delete-btn"
                        >
                          {t.delete}
                        </button>

                        <button onClick={() => handleMoveBookingClick(index)} className="move-btn">{t.move}</button>
                        <button onClick={() => handleBlockClient(booking.user, booking.id)} className="block-btn">{t.block}</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* ✅ kiválasztott kliens csomagjai */}
            <div className="client-packages-section">
              <div className="client-packages-header">
                <strong>{t.client_packages}</strong>
              </div>

              <div className="client-packages-picker">
                <label>{t.select_client}:</label>
                <select
                  value={selectedClientBookingId}
                  onChange={(e) => setSelectedClientBookingId(e.target.value)}
                >
                  {localBookings.map((b) => (
                    <option key={b.id} value={String(b.id)}>
                      {b.booked_by_name || t.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* ✅ ha nincs user (book-by-name), akkor külön üzenet */}
              {!selectedUserId ? (
                <p style={{ marginTop: 10 }}>
                  {t.no_user_for_selected_booking}
                </p>
              ) : clientPackagesLoading ? (
                <p style={{ marginTop: 10 }}>{t.loading_data}</p>
              ) : clientPackages.length === 0 ? (
                <p style={{ marginTop: 10 }}>{t.no_packages_for_appointment}</p>
              ) : (
                <table className="client-packages-table">
                  <thead>
                    <tr>
                      <th>{t.package_name}</th>
                      <th>{t.description}</th>
                      <th>{t.price}</th>
                      <th>{t.duration}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientPackages.map((pkg) => {
                      const dur = Number(pkg.service_package_duration || 0);
                      const h = Math.floor(dur);
                      const m = Math.round((dur % 1) * 60);

                      return (
                        <tr key={pkg.id}>
                          <td>{pkg.service_package_name}</td>
                          <td>{pkg.service_package_description}</td>
                          <td>{pkg.service_package_price} RON</td>
                          <td>{h} {t.hours} {m} {t.minutes}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {(isMovingBooks || isMovingAppointment) ? (
          <div className="calendar-container" style={{ maxHeight: '400px', overflowY: 'scroll' }}>
            <FullCalendar {...calendarOptions} />
            <button onClick={handleClose} className="cancel-btn">{t.cancel}</button>
          </div>
        ) : showConfirm ? (
          <div className="confirm-delete">
            <p>{t.confirm_delete}</p>
            <button onClick={handleConfirmDelete} className="confirm-btn">{t.yes}</button>
            <button onClick={() => setShowConfirm(false)} className="cancel-btn">{t.no}</button>
          </div>
        ) : showBlockConfirm ? (
          <div className="confirm-delete">
            <p>{t.confirm_block}</p>
            <button onClick={confirmBlockClient} className="confirm-btn">{t.yes}</button>
            <button onClick={() => setShowBlockConfirm(false)} className="cancel-btn">{t.no}</button>
          </div>
        ) : isBooking ? (
          <div className="booking-form">
            <p>{t.enter_name_prompt}</p>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.name_placeholder}
            />
            <div>
              <button onClick={() => onBook(name)} className="confirm-btn">{t.book}</button>
              <button onClick={handleClose} className="cancel-btn">{t.cancel}</button>
            </div>
          </div>
        ) : (
          <div className="modal-actions">
            <button onClick={() => setShowConfirm(true)} className="delete-btn">{t.delete_appointment}</button>
            <button onClick={() => setisMovingAppointment(true)} className="move-btn">{t.move_appointment}</button>
            <button onClick={() => setIsBooking(true)} className="rename-btn">{t.book_by_name}</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDetailsModal;
